import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import { API_CONFIG } from '../config/api';

interface Social {
  id: string;
  type: string;
  url: string;
  createdAt: string;
}

interface SocialState {
  socials: Social[];
  isLoading: boolean;
  error: string | null;
}

export const useSocials = () => {
  const [socialState, setSocialState] = useState<SocialState>({
    socials: [],
    isLoading: false,
    error: null,
  });

  const { fetchWithAuth } = useFetch();

  const fetchSocials = useCallback(async (): Promise<void> => {
    setSocialState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await fetchWithAuth(`${API_CONFIG.BASE_URL}/socials`);

      // Se for 404 ou 500, considerar como lista vazia (sem erro)
      if (!response.ok) {
        if (response.status === 404 || response.status === 500) {
          setSocialState(prev => ({
            ...prev,
            socials: [],
            isLoading: false,
            error: null,
          }));
          return;
        }
        
        const errorText = await response.text();
        throw new Error(`Erro ao buscar socials: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // A API retorna {socials: Array}, então extraímos o array
      const socialsData = data.socials || [];

      setSocialState(prev => ({
        ...prev,
        socials: socialsData,
        isLoading: false,
      }));
    } catch (error) {
      setSocialState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  const resetSocialState = () => {
    setSocialState({
      socials: [],
      isLoading: false,
      error: null,
    });
  };

  return {
    fetchSocials,
    socialState,
    resetSocialState,
  };
};
